import type {PaymentNotifications,PaymentNotificationDelivery} from './payment-notification-types.js';
import { z } from "zod";
import catalog from "./commerce-catalog.json" with { type: "json" };

export const CommerceProductSchema = z.enum(["payments", "crypto", "domains"]);
export const CommercePaymentProductSchema = z.enum(["payments", "crypto"]);
export const CommerceModeSchema = z.enum(['live','preview']);
export const CommerceCheckoutInput = z.object({requestId:z.uuid(),offerId:z.uuid()}).strict();
export const CommerceOfferStateInput = z.object({requestId:z.uuid(),state:z.enum(['published','archived'])}).strict();
export type CommerceProduct = z.infer<typeof CommerceProductSchema>;
export const COMMERCE = catalog;
export function commerceProduct(id: CommerceProduct) {
  return COMMERCE.products.find((p) => p.id === id)!;
}
export const CommerceProfileInput = z
  .object({
    requestId: z.uuid(),
    displayName: z.string().trim().min(2).max(100),
    supportEmail: z.email().max(254),
  })
  .strict();
export const CommerceOfferInput = z
  .object({
    requestId: z.uuid(),
    product: CommercePaymentProductSchema,
    name: z.string().trim().min(2).max(100),
    description: z.string().trim().max(1000),
    price: z.string().regex(/^(0|[1-9]\d{0,8})(\.\d{1,18})?$/),
    assetId: z.string().max(80).optional(),
    interval: z.enum(["once", "month", "year"]),
  })
  .strict();
export const CommerceDomainInput = z
  .object({
    requestId: z.uuid(),
    domain: z.string().trim().min(4).max(253),
    renewalReminder: z.boolean(),
    applicationName: z.string().trim().max(100),
  })
  .strict();
export const CommerceDnsInput = z
  .object({
    requestId: z.uuid(),
    type: z.enum(["A", "AAAA", "CNAME", "TXT", "MX"]),
    name: z.string().trim().min(1).max(253),
    value: z.string().trim().min(1).max(1024),
    priority: z.number().int().min(0).max(65535).nullable(),
  })
  .strict();
export const CommerceTokenInput = z
  .object({
    requestId: z.uuid(),
    name: z.string().trim().min(2).max(80),
    product: CommercePaymentProductSchema,
    mode: CommerceModeSchema.default('preview'),
    lifetime: z.enum(['30-days','application']).default('30-days'),
  })
  .strict();
export type CommerceProfile = {
  displayName: string;
  supportEmail: string;
  updatedAt: string;
};
export const CommerceOfferSchema = z.object({
  id:z.uuid(),product:CommerceProductSchema,name:z.string(),description:z.string(),
  amountAtomic:z.string().regex(/^\d+$/),currency:z.string(),decimals:z.number().int().nonnegative(),
  network:z.string().nullable(),interval:z.enum(['once','month','year']),
  state:z.enum(['draft','published','archived']),createdAt:z.iso.datetime(),
}).strict();
export type CommerceOffer = z.infer<typeof CommerceOfferSchema>;
export type CommerceOrder = {
  id: string;
  product: CommerceProduct;
  description: string;
  amountAtomic: string | null;
  currency: string;
  decimals: number;
  network: string | null;
  state:
    | "open"
    | "completed"
    | "failed"
    | "refunded"
    | "requested"
    | "cancelled";
  createdAt: string;
  expiresAt: string | null;
  checkoutPath: string | null;
};
export type CommerceDomain = {
  id: string;
  domain: string;
  orderId: string;
  state: "requested" | "cancelled";
  renewalReminder: boolean;
  applicationName: string;
  records: {
    id: string;
    type: string;
    name: string;
    value: string;
    priority: number | null;
  }[];
  createdAt: string;
};
export type CommerceEvent = {
  id: string;
  orderId: string | null;
  product: CommerceProduct | null;
  kind: string;
  createdAt: string;
};
export type CommercePolicy = {
  product: CommerceProduct;
  previewEnabled: boolean;
  productName: string;
  feeBasisPoints: number | null;
  feeFixedMinor: number | null;
  updatedAt: string;
};
export type CommerceToken = {
  id: string;
  name: string;
  product: CommerceProduct;
  mode: z.infer<typeof CommerceModeSchema>;
  prefix: string;
  createdAt: string;
  expiresAt: string | null;
  revokedAt: string | null;
};
export type CommerceWorkspace = {
  notifications:PaymentNotifications;
  mode: "preview";
  profile: CommerceProfile | null;
  offers: CommerceOffer[];
  orders: CommerceOrder[];
  domains: CommerceDomain[];
  events: CommerceEvent[];
  policies: CommercePolicy[];
  tokens: CommerceToken[];
  totals: {
    currency: string;
    decimals: number;
    network: string | null;
    grossAtomic: string;
    refundedAtomic: string;
    netAtomic: string;
  }[];
  hasMore: boolean;
};
export type CommerceAvailability=Record<CommerceProduct,{enabled:boolean}>;
export type CommerceAdmin = {
  availability:CommerceAvailability;
  notificationFailures:{items:(PaymentNotificationDelivery&{accountId:string})[];hasMore:boolean};
  mode: "preview";
  policies: CommercePolicy[];
  counts: {
    merchants: number;
    offers: number;
    orders: number;
    domains: number;
  };
  orders: (CommerceOrder & { accountId: string; username: string })[];
  merchants: {
    accountId: string;
    username: string;
    displayName: string;
    supportEmail: string;
    updatedAt: string;
  }[];
  events: (CommerceEvent & { accountId: string })[];
  hasMore: boolean;
};
export type CommerceCheckout = {
  mode: "preview";
  order: CommerceOrder;
  merchant: string;
  supportEmail: string;
  offerName: string;
  interval: string;
  canComplete: boolean;
};

/** Integer arithmetic; USD displays two places, crypto quantities retain their registered asset precision. Never combine networks. */
export function commerceAmount(
  atomic: string | null,
  currency: string,
  decimals: number,
): string {
  if (atomic === null) return "Quote required";
  const n = BigInt(atomic),
    negative = n < 0n,
    value = negative ? -n : n,
    scale = 10n ** BigInt(decimals);
  const whole = (value / scale)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ","),
    fraction = (value % scale).toString().padStart(decimals, "0");
  return `${negative ? "-" : ""}${currency === "USD" ? "$" : ""}${whole}.${fraction}${currency === "USD" ? "" : ` ${currency}`}`;
}
